import { isLlm, Llm, SupportedLlm } from './models/llm.consts';

/**
 * DELETED - Message was deleted
 * ERROR - An error occurred while generating this message
 * OK - The message was created (and generated if not a user message) successfully
 * PENDING - The message is in the process of being generated
 * QUEUED - The message is queued to be processed in the future
 *   - E.g. A workflow step that has not began execution yet
 * READY - The message was queued, and is now awaiting user action to begin processing
 */
export enum ChatMessageState {
  DELETED = 'deleted',
  ERROR = 'error',
  OK = 'ok',
  PENDING = 'pending',
  QUEUED = 'queued',
  READY = 'ready',
  STOPPED = 'stopped'
}

export enum ChatMessageRole {
  ASSISTANT = 'assistant',
  DEVELOPER = 'developer',
  SYSTEM = 'system',
  TOOL = 'tool',
  USER = 'user'
}

export enum ChatMessageContentType {
  KNOWLEDGE_STORE = 'KNOWLEDGE_STORE',
  LIBRARY_SEARCH = 'LIBRARY_SEARCH',
  OUTPUT = 'OUTPUT',
  SANDBOX_ARTIFACT = 'SANDBOX_ARTIFACT',
  SPECIFIED = 'SPECIFIED'
}

export enum ContentMode {
  ALL = 'All',
  CSV_ROW_BY_ROW = 'Row-By-Row',
  PARALLEL = 'Parallel'
}

export enum ChatListWorkflowRunTypeFilter {
  ALL = 'ALL',
  // Chat generated from agent/workflow with 0 steps, only configuration specs defined
  // (e.g., name, description, instructions)
  CHATBOT = 'CHATBOT',
  // Chat generated from agent/workflow with 1+ llm steps
  MANUAL = 'MANUAL',
  // Chat generated from agent/workflow with 1+ triggers defined on it
  TRIGGERED = 'TRIGGERED'
}

export enum ChatListAccessScopeFilter {
  // If admin, includes all chats created by any org member
  // If member, includes the union of CREATOR_ROLE and SHARED_ROLE
  ANY_SCOPE = 'ANY_SCOPE',
  // User is the creator of the chat
  CREATOR_SCOPE = 'CREATOR_SCOPE',
  // User is shared on the chat
  // i.e., orgRole is either EDIT or VIEW and user belongs to the same org as the chat's creator
  SHARED_SCOPE = 'SHARED_SCOPE'
}

export const GEMINI_CHAT_LLMS = [
  Llm.GEMINI_2_0_FLASH,
  Llm.GEMINI_2_0_FLASH_LITE,
  Llm.GEMINI_2_5_PRO
] as const satisfies readonly SupportedLlm[];

// DO NOT REMOVE COMMENT: Llm
export const isGeminiChatLlm = (model: string): boolean =>
  isLlm(model) && GEMINI_CHAT_LLMS.includes(model as any);

// These models are displayed as an option for chat
// and workflows to all users.
// Note: The order of the list is also the order they're presented to the user
export const DISPLAYED_CHAT_LLMS = [
  Llm.GEMINI_2_0_FLASH,
  Llm.GEMINI_2_0_FLASH_LITE,
  Llm.GEMINI_2_5_PRO,
  Llm.LLAMA_3_3_70B,
  // Llm.LLAMA_3_1_8B_INSTANT, # Hiding because it's not a great chat model
  Llm.CLAUDE_3_5_HAIKU,
  Llm.CLAUDE_3_5_SONNET,
  Llm.CLAUDE_3_7_SONNET,
  Llm.CLAUDE_3_OPUS,
  Llm.DEEPSEEK_R1,
  Llm.GPT_4O,
  Llm.GPT_4_1,
  Llm.GPT_4_1_MINI,
  Llm.O1,
  Llm.O3,
  Llm.O4_MINI,
  Llm.QWEN2_72B_INSTRUCT,
  Llm.QWEN2_5_72B_INSTRUCT_TURBO,
  Llm.WHITERABBITNEO_2_5_QWEN_2_5_32B,
  Llm.WHITERABBITNEO_33B,
  Llm.WHITERABBITNEO_V3_7B
] as const satisfies readonly SupportedLlm[];

export enum ChatAccessLevel {
  EDIT = 'EDIT',
  RESTRICTED = 'RESTRICTED',
  VIEW = 'VIEW'
}

export function isChatAccessLevel(
  accessLevel: string
): accessLevel is ChatAccessLevel {
  return Object.values(ChatAccessLevel).includes(
    accessLevel as ChatAccessLevel
  );
}

// Possible roles for ChatUserGroupRole
export enum ChatUserGroupRole {
  EDITOR = 'EDITOR',
  VIEWER = 'VIEWER'
}

export function isChatUserGroupRole(
  role: string | undefined
): role is ChatUserGroupRole {
  return Object.values(ChatUserGroupRole).includes(role as ChatUserGroupRole);
}

export enum SupportedExecutableLanguage {
  PYTHON = 'python'
}

export const SUPPORTED_EXECUTABLE_LANGUAGES = [
  SupportedExecutableLanguage.PYTHON
] as const satisfies readonly SupportedExecutableLanguage[];

// This is the message that is substituted for error messages in chat history
// before sending to an LLM in order to support history retention and LLMs that require
// alternating message roles.
export const CHAT_HISTORY_ERROR_MESSAGE = 'There was an error.';

export const LLAMA_INDEXER_GENERATE_CHAT_PATH = '/generate-chat';

export const LLAMA_INDEXER_GENERATE_MULTIMODAL_CHAT_PATH =
  '/generate-multimodal-chat';

export const LLAMA_INDEXER_GENERATE_AGENT_CHAT_PATH = '/generate-agent-chat';

export const LLAMA_INDEXER_RUN_CODE_PATH = '/run-code';

export const LLAMA_INDEXER_RUN_API_REQUEST_PATH = '/run-api-request';
